import type { ReactNode } from "react";

interface SectionProps {
  children: ReactNode;
  className?: string;
  tone?: "default" | "lavender" | "ink" | "image";
  id?: string;
  /** Background photo URL, used with tone="image". */
  backgroundImage?: string;
  /** Tailwind opacity class for the dark overlay, e.g. "bg-black/65". */
  overlayClassName?: string;
  /** Override vertical padding classes for tone="image" sections. */
  paddingClassName?: string;
}

export function Section({
  children,
  className = "",
  tone = "default",
  id,
  backgroundImage,
  overlayClassName = "bg-black/65",
  paddingClassName = "py-14 md:py-28",
}: SectionProps) {
  if (tone === "image" && backgroundImage) {
    return (
      <section id={id} className={`relative isolate overflow-hidden ${paddingClassName}`}>
        <img
          src={backgroundImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className={`absolute inset-0 -z-10 ${overlayClassName}`} aria-hidden="true" />
        <div className={`relative mx-auto max-w-6xl px-6 ${className}`}>{children}</div>
      </section>
    );
  }

  const toneClass =
    tone === "lavender"
      ? "bg-lavender"
      : tone === "ink"
        ? "bg-ink text-primary-foreground"
        : "bg-background";
  return (
    <section id={id} className={`${toneClass} ${paddingClassName}`}>
      <div className={`mx-auto max-w-6xl px-6 ${className}`}>{children}</div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  intro,
  align = "left",
  onDark = false,
  titleClassName,
  titleStyle,
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
  align?: "left" | "center";
  onDark?: boolean;
  /** Replaces the default title typography classes when provided. */
  titleClassName?: string;
  titleStyle?: React.CSSProperties;
}) {
  return (
    <div className={`mb-8 max-w-3xl md:mb-12 ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && (
        <p
          className={`mb-3 text-xs font-semibold uppercase tracking-[0.18em] ${
            onDark ? "text-white/80" : "text-primary"
          }`}
        >
          {eyebrow}
        </p>
      )}
      <h2
        className={
          titleClassName
            ? titleClassName
            : `font-display text-3xl leading-tight tracking-tight md:text-4xl lg:text-5xl ${
                onDark ? "text-white" : "text-ink"
              }`
        }
        style={titleStyle}
      >
        {title}
      </h2>
      {intro && (
        <p
          className={`mt-4 text-base leading-relaxed md:mt-5 md:text-lg ${
            onDark ? "text-white/80" : "text-muted-foreground"
          }`}
        >
          {intro}
        </p>
      )}
    </div>
  );
}
